const KETODO_REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

function clamp(value, min = 0, max = 1) {
  return Math.min(max, Math.max(min, value));
}


function getKetodoScrollTarget(element) {
  let node = element;
  while (node && node !== document.documentElement) {
    const style = getComputedStyle(node);
    if (/(auto|scroll)/.test(style.overflowY) && node.scrollHeight > node.clientHeight + 1) return node;
    node = node.parentElement;
  }
  const wrapper = document.querySelector('.page-wrapper');
  if (wrapper && wrapper.scrollHeight > wrapper.clientHeight + 1) return wrapper;
  return window;
}

function getKetodoScrollPosition(element) {
  const target = getKetodoScrollTarget(element);
  return target === window ? (window.scrollY || document.documentElement.scrollTop || 0) : target.scrollTop;
}

function listenKetodoScroll(element, handler) {
  getKetodoScrollTarget(element).addEventListener('scroll', handler, { passive: true });
}

function initKetodoHeader(root = document) {
  root.querySelectorAll('[data-ketodo-header]').forEach((header) => {
    if (header.dataset.ketodoReady) return;
    header.dataset.ketodoReady = 'true';
    const threshold = Number(header.dataset.threshold || 48);
    const update = () => header.classList.toggle('is-scrolled', getKetodoScrollPosition(header) > threshold);
    update();
    listenKetodoScroll(header, update);
  });
}

function initKetodoReveal(root = document) {
  const nodes = [...root.querySelectorAll('.ketodo-reveal:not([data-ketodo-ready])')];
  if (!nodes.length) return;
  nodes.forEach((node) => (node.dataset.ketodoReady = 'true'));
  if (KETODO_REDUCED.matches || !('IntersectionObserver' in window)) {
    nodes.forEach((node) => node.classList.add('is-visible'));
    return;
  }
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('is-visible');
      observer.unobserve(entry.target);
    });
  }, { threshold: 0.16, rootMargin: '0px 0px -8% 0px' });
  nodes.forEach((node) => observer.observe(node));
}

function initKetodoHero(root = document) {
  root.querySelectorAll('[data-ketodo-hero]').forEach((hero) => {
    if (hero.dataset.ketodoReady || KETODO_REDUCED.matches || hero.dataset.parallax !== 'true') return;
    hero.dataset.ketodoReady = 'true';
    const media = hero.querySelector('.ketodo-hero__media');
    const intensity = Number(hero.dataset.intensity || 12);
    let active = false;
    let raf = 0;
    const draw = () => {
      raf = 0;
      if (!active) return;
      const rect = hero.getBoundingClientRect();
      const progress = clamp(-rect.top / Math.max(1, rect.height));
      media.style.translate = `0 ${progress * intensity}%`;
    };
    const queue = () => { if (!raf) raf = requestAnimationFrame(draw); };
    const observer = new IntersectionObserver(([entry]) => { active = entry.isIntersecting; if (active) queue(); }, { rootMargin: '20% 0px' });
    observer.observe(hero);
    listenKetodoScroll(hero, queue);
  });
}

function initKetodoIngredients(root = document) {
  root.querySelectorAll('[data-ketodo-ingredients]').forEach((section) => {
    if (section.dataset.ketodoReady) return;
    section.dataset.ketodoReady = 'true';
    const items = [...section.querySelectorAll('[data-ingredient]')];
    const plate = section.querySelector('[data-ketodo-plate]');
    const intensity = Number(section.dataset.intensity || 100) / 100;
    const enable3d = section.dataset.enable3d === 'true';
    let active = false;
    let raf = 0;
    const interpolate = (a, b, p) => a + (b - a) * p;
    const draw = () => {
      raf = 0;
      if (!active || KETODO_REDUCED.matches) return;
      const rect = section.getBoundingClientRect();
      const range = Math.max(1, rect.height - window.innerHeight);
      const progress = clamp(-rect.top / range);
      section.style.setProperty('--k-progress', progress.toFixed(4));
      if (plate) {
        const plateScale = 1 + Math.sin(progress * Math.PI) * .08 * intensity;
        const tilt = enable3d ? Math.sin(progress * Math.PI * 2) * 5 * intensity : 0;
        plate.style.transform = `scale(${plateScale}) rotateX(${tilt}deg) rotateZ(${-tilt * .35}deg)`;
      }
      const coordinateScale = plate ? Math.min(1, plate.getBoundingClientRect().width / 600) : 1;
      items.forEach((item) => {
        const enter = Number(item.dataset.enter || 0);
        const exit = Number(item.dataset.exit || 1);
        const local = clamp((progress - enter) / Math.max(.01, exit - enter));
        const eased = local * local * (3 - 2 * local);
        const sx = Number(item.dataset.sx || 0), sy = Number(item.dataset.sy || 0);
        const ex = Number(item.dataset.ex || 0), ey = Number(item.dataset.ey || 0);
        const ss = Number(item.dataset.ss || 1), es = Number(item.dataset.es || 1);
        const sr = Number(item.dataset.sr || 0), er = Number(item.dataset.er || 0);
        const startOpacity = Number(item.dataset.so || 0), endOpacity = Number(item.dataset.eo || 1);
        let x = interpolate(sx * coordinateScale, ex * coordinateScale * intensity, eased);
        let y = interpolate(sy * coordinateScale, ey * coordinateScale * intensity, eased);
        const path = item.dataset.path;
        if (path === 'arc') y -= Math.sin(eased * Math.PI) * 80 * coordinateScale * intensity;
        if (path === 'wave') x += Math.sin(eased * Math.PI * 2) * 36 * coordinateScale * intensity;
        const scale = interpolate(ss, es, eased);
        const rotation = interpolate(sr, er, eased);
        const opacity = interpolate(startOpacity, endOpacity, eased);
        const depth = enable3d ? Number(item.dataset.depth || 0) * eased * intensity : 0;
        item.style.transform = `translate3d(calc(-50% + ${x}px),calc(-50% + ${y}px),${depth}px) scale(${scale}) rotate(${rotation}deg)`;
        item.style.opacity = opacity.toFixed(3);
        const labelIn = clamp((local - .18) * 4);
        const labelOut = 1 - clamp((local - .72) * 4);
        item.style.setProperty('--k-label-opacity', (labelIn * labelOut).toFixed(3));
      });
    };
    const queue = () => { if (!raf) raf = requestAnimationFrame(draw); };
    const observer = new IntersectionObserver(([entry]) => { active = entry.isIntersecting; if (active) queue(); }, { rootMargin: '15% 0px' });
    observer.observe(section);
    listenKetodoScroll(section, queue);
    window.addEventListener('resize', queue, { passive: true });
    draw();
  });
}

function initKetodoTilt(root = document) {
  if (KETODO_REDUCED.matches || !window.matchMedia('(pointer:fine)').matches) return;
  root.querySelectorAll('[data-ketodo-tilt="true"]').forEach((card) => {
    if (card.dataset.ketodoTiltReady) return;
    card.dataset.ketodoTiltReady = 'true';
    card.addEventListener('pointermove', (event) => {
      const rect = card.getBoundingClientRect();
      const x = (event.clientX - rect.left) / rect.width - .5;
      const y = (event.clientY - rect.top) / rect.height - .5;
      card.style.transform = `perspective(900px) rotateX(${-y * 5}deg) rotateY(${x * 5}deg) translateY(-5px)`;
    });
    card.addEventListener('pointerleave', () => { card.style.transform = ''; });
  });
}


function initKetodoEditorial(root = document) {
  root.querySelectorAll('[data-ketodo-panel]').forEach((panel) => {
    if (panel.dataset.ketodoMotionReady || KETODO_REDUCED.matches) return;
    panel.dataset.ketodoMotionReady = 'true';
    let active = false;
    let raf = 0;
    const draw = () => {
      raf = 0;
      if (!active) return;
      const rect = panel.getBoundingClientRect();
      const progress = clamp((window.innerHeight - rect.top) / Math.max(1, window.innerHeight + rect.height));
      const centered = progress - .5;
      panel.style.setProperty('--k-panel-shift', `${(-centered * 54).toFixed(2)}px`);
      panel.style.setProperty('--k-copy-shift', `${(centered * 20).toFixed(2)}px`);
    };
    const queue = () => { if (!raf) raf = requestAnimationFrame(draw); };
    const observer = new IntersectionObserver(([entry]) => {
      active = entry.isIntersecting;
      if (active) queue();
    }, { rootMargin: '18% 0px' });
    observer.observe(panel);
    listenKetodoScroll(panel, queue);
    window.addEventListener('resize', queue, { passive: true });
  });
}

function initKetodoFinal(root = document) {
  root.querySelectorAll('[data-ketodo-final]').forEach((section) => {
    if (section.dataset.ketodoMotionReady || KETODO_REDUCED.matches) return;
    section.dataset.ketodoMotionReady = 'true';
    let active = false;
    let raf = 0;
    const draw = () => {
      raf = 0;
      if (!active) return;
      const rect = section.getBoundingClientRect();
      const progress = clamp((window.innerHeight - rect.top) / Math.max(1, window.innerHeight + rect.height));
      section.style.setProperty('--k-final-media-shift', `${((progress - .5) * 52).toFixed(2)}px`);
      section.style.setProperty('--k-final-copy-shift', `${((.5 - progress) * 18).toFixed(2)}px`);
    };
    const queue = () => { if (!raf) raf = requestAnimationFrame(draw); };
    const observer = new IntersectionObserver(([entry]) => {
      active = entry.isIntersecting;
      if (active) queue();
    }, { rootMargin: '18% 0px' });
    observer.observe(section);
    listenKetodoScroll(section, queue);
    window.addEventListener('resize', queue, { passive: true });
  });
}

function initKetodo(root = document) {
  initKetodoHeader(root);
  initKetodoReveal(root);
  initKetodoHero(root);
  initKetodoIngredients(root);
  initKetodoEditorial(root);
  initKetodoFinal(root);
  initKetodoTilt(root);
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initKetodo());
else initKetodo();
document.addEventListener('shopify:section:load', (event) => initKetodo(event.target));
